import { createTheme } from "@mui/material/styles";
import { tokens } from "./tokens";

export const DEFAULT_THEME_SETTINGS = Object.freeze({
  mode: "dark",
  primaryColor: tokens.colors.primary,
  borderRadius: 8,
});

const clampBorderRadius = (value) => {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return DEFAULT_THEME_SETTINGS.borderRadius;
  return Math.min(24, Math.max(0, Math.round(parsedValue)));
};

export const normalizeThemeSettings = (settings = {}) => ({
  mode: settings.mode === "light" ? "light" : "dark",
  primaryColor:
    (typeof settings.primaryColor === "string" && settings.primaryColor) ||
    (typeof settings.color?.[500] === "string" && settings.color[500]) ||
    DEFAULT_THEME_SETTINGS.primaryColor,
  borderRadius: clampBorderRadius(settings.borderRadius),
});

export const createAppTheme = (preferences = {}) => {
  const settings = normalizeThemeSettings(preferences);
  const isDark = settings.mode === "dark";
  const surface = isDark ? tokens.colors.surface : "rgba(255,255,255,0.92)";
  const surfaceBorder = isDark
    ? tokens.colors.border
    : "rgba(24,39,63,0.12)";
  const textPrimary = isDark ? tokens.colors.text : "rgba(24,39,63,0.92)";
  const textSecondary = isDark ? tokens.colors.muted : "rgba(24,39,63,0.64)";

  return createTheme({
    palette: {
      mode: settings.mode,
      primary: { main: settings.primaryColor },
      secondary: { main: tokens.colors.primary2 },
      error: { main: tokens.colors.error },
      success: { main: tokens.colors.success },
      background: {
        default: isDark ? tokens.colors.bg0 : "#f3f6f9",
        paper: surface,
      },
      text: {
        primary: textPrimary,
        secondary: textSecondary,
      },
      divider: isDark ? "rgba(255,255,255,0.08)" : "rgba(24,39,63,0.10)",
    },

    shape: { borderRadius: settings.borderRadius },

    typography: {
      fontFamily: [
        "Inter",
        "system-ui",
        "-apple-system",
        "Segoe UI",
        "Roboto",
        "Arial",
      ].join(","),
      h4: { fontWeight: 900, letterSpacing: 0 },
      h5: { fontWeight: 900, letterSpacing: 0 },
      h6: { fontWeight: 900, letterSpacing: 0 },
      body1: { fontWeight: 600 },
      body2: { fontWeight: 600 },
      button: { textTransform: "none", fontWeight: 900, letterSpacing: 0 },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background: isDark
              ? `linear-gradient(180deg, ${tokens.colors.bg1} 0%, ${tokens.colors.bg0} 55%, ${tokens.colors.bg0} 100%)`
              : "linear-gradient(180deg, #f8fafc 0%, #eef3f7 100%)",
            minHeight: "100vh",
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: settings.borderRadius,
            border: `1px solid ${surfaceBorder}`,
            background: surface,
            backdropFilter: "blur(10px)",
            boxShadow: tokens.shadow.neumorph,
          },
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: settings.borderRadius,
            border: `1px solid ${surfaceBorder}`,
            background: surface,
            backdropFilter: "blur(10px)",
            boxShadow: tokens.shadow.neumorph,
          },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: settings.borderRadius,
            padding: "12px 16px",
          },
          containedPrimary: {
            boxShadow: "0 14px 26px rgba(0,0,0,0.25)",
          },
          outlined: {
            borderColor: surfaceBorder,
            color: textPrimary,
            background: isDark
              ? "rgba(255,255,255,0.04)"
              : "rgba(24,39,63,0.03)",
          },
        },
      },

      MuiTextField: {
        defaultProps: {
          variant: "outlined",
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: settings.borderRadius,
            background: isDark
              ? "rgba(255,255,255,0.04)"
              : "rgba(24,39,63,0.03)",
            boxShadow: tokens.shadow.inset,
            "& fieldset": {
              borderColor: surfaceBorder,
            },
            "&:hover fieldset": {
              borderColor: settings.primaryColor,
            },
            "&.Mui-focused fieldset": {
              borderColor: settings.primaryColor,
            },
          },
          input: {
            padding: "14px 16px",
          },
        },
      },

      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: isDark
              ? "rgba(255,255,255,0.08)"
              : "rgba(24,39,63,0.10)",
          },
        },
      },

      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            background: surface,
            backdropFilter: "blur(10px)",
            boxShadow: tokens.shadow.neumorph,
            height: 70,
            paddingBottom: 8,
          },
        },
      },

      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            color: textSecondary,
            "&.Mui-selected": {
              color: settings.primaryColor,
            },
          },
          label: {
            fontWeight: 800,
          },
        },
      },
    },
  });
};

export const theme = createAppTheme(DEFAULT_THEME_SETTINGS);
